import { FormEvent, UIEvent, useState } from "react";
import { ArrowRight, Waves } from "lucide-react";
import { useRouter } from "next/router";
import { LoadingButton } from "@/components/ui/loading-button";
import Link from "next/link";
import { AnimatedAgentsSection } from "@/components/boardblend/animated-agents-section";
import { IntegrationsBand } from "@/components/boardblend/integrations-band";
import { HomeProductVideo } from "@/components/boardblend/home-product-video";
import { ShowcaseSection } from "@/components/boardblend/showcase-section";
import { HeroCursors } from "@/components/boardblend/hero-cursors";
import { HomeFaqSection } from "@/components/boardblend/home-faq-section";
import { HomeFooter } from "@/components/boardblend/home-footer";
import { HomePricingSection } from "@/components/boardblend/home-pricing-section";
import { HomeScrollHeader } from "@/components/boardblend/home-scroll-header";
import { RoomCodeInput } from "@/components/boardblend/room-code-input";
import { getApiErrorCode, getApiErrorMessage } from "@/store/api";
import {
  useCreateSessionMutation,
  useJoinSessionMutation,
} from "@/store/entry-api";

export function HomeScreen() {
  const router = useRouter();
  const [roomCode, setRoomCode] = useState("");
  const [showScrollHeader, setShowScrollHeader] = useState(false);
  const [createSession, createState] = useCreateSessionMutation();
  const [joinSession, joinState] = useJoinSessionMutation();
  const busy = createState.isLoading || joinState.isLoading;
  const canJoin = Boolean(roomCode.trim());

  const create = async (event: FormEvent) => {
    event.preventDefault();
    createState.reset();

    try {
      const result = await createSession().unwrap();
      await router.push(`/session/${result.session.id}`);
    } catch (cause) {
      if (getApiErrorCode(cause) === "OWNED_SESSION_LIMIT") {
        await router.push("/dashboard?upgrade=monthly");
      }
      // RTK Query exposes the request error through createState.
    }
  };

  const join = async (code = roomCode) => {
    joinState.reset();

    try {
      const name = getRememberedName() || "Anonymous";
      const result = await joinSession({
        displayName: name,
        roomCode: code,
      }).unwrap();
      await router.push(`/session/${result.session.id}`);
    } catch (cause) {
      // RTK Query exposes the request error through joinState.
    }
  };

  const trackScroll = (event: UIEvent<HTMLElement>) => {
    setShowScrollHeader(event.currentTarget.scrollTop > 96);
  };

  return (
    <main className="home-screen" onScroll={trackScroll}>
      <HomeScrollHeader visible={showScrollHeader} />
      <section className="home-hero" id="top">
        <HeroCursors />
        <div className="home-brand">
          <span className="brand-mark" aria-hidden="true">
            <Waves />
          </span>
          <strong>Boardblend</strong>
        </div>
        <div className="home-copy">
          <p className="eyebrow">AI BRAINSTORMING FOR TEAMS</p>
          <h1>Stronger team decisions, powered by everyone’s own AI.</h1>
          <p>
            Each teammate brings their own AI agent that already knows their
            domain, context, workflows, and expertise into one shared
            brainstorming canvas.
          </p>
          <form className="create-form" onSubmit={create}>
            <div className="create-action">
              <LoadingButton
                type="submit"
                loading={createState.isLoading}
                disabled={busy}
              >
                Start a brainstorm <ArrowRight />
              </LoadingButton>
              <p className="create-note">No sign up required</p>
            </div>
            {createState.error &&
              getApiErrorCode(createState.error) !== "OWNED_SESSION_LIMIT" && (
                <p className="form-error" role="alert">
                  {getApiErrorMessage(
                    createState.error,
                    "Something went wrong",
                  )}
                </p>
              )}
          </form>
        </div>
      </section>

      <aside className="join-panel" aria-labelledby="join-heading">
        <div>
          <p className="eyebrow">Have a room code?</p>
          <h2 id="join-heading">Join the conversation</h2>
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void join();
          }}
        >
          <label htmlFor="room-code">Room code</label>
          <RoomCodeInput
            id="room-code"
            value={roomCode}
            disabled={busy}
            onChange={setRoomCode}
            onComplete={(code) => void join(code)}
          />
          {joinState.error && (
            <p className="form-error" role="alert">
              {getApiErrorMessage(joinState.error, "Something went wrong")}
            </p>
          )}
          <LoadingButton
            type="submit"
            variant="outline"
            loading={joinState.isLoading}
            disabled={busy || !canJoin}
          >
            Join room
          </LoadingButton>
        </form>
        <p className="subtle-sign-in">
          Have an account? <Link href="/sign-in">Sign in</Link>
          <br />
          New here? <Link href="/sign-up">Create an account</Link>
        </p>
      </aside>
      <HomeProductVideo />
      <IntegrationsBand />
      <ShowcaseSection />
      <AnimatedAgentsSection />
      <HomePricingSection />
      <HomeFaqSection />
      <HomeFooter />
    </main>
  );
}

export function getRememberedName() {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("boardblend-display-name") ?? "";
}
